/**
 * The quick price range (plan §5.11).
 *
 * As soon as a salesperson has typed the model year, the reference price and the odometer — before any
 * inspection — this produces an indicative band from four scenarios: good and poor condition, at low
 * and high mileage. It is computed with **the real equations and the real template values**, so the
 * number a customer hears first and the number the detailed appraisal lands on come from the same
 * policy; it is never stored as a valuation and never feeds one. The salesperson may keep the closest
 * scenario as a label, which is all it is.
 */
import { runPolicy } from '../expressions/policy-runner.js';
import { buildValuationInputs } from '../valuation/build-inputs.js';
/** Condition profiles the scenarios price (plan §5.11). */
const GOOD = { grade7: 'G7', severity: 'EXCELLENT' };
const POOR = { grade7: 'G2', severity: 'MAJOR' };
/**
 * The facelift and customs flags are taken **as entered**: an unanswered flag is not silently read as
 * "does not apply", it simply leaves the scenario unpriced and the caller labels the range incomplete.
 * The damage groups are always "none" — nobody has inspected the car yet.
 */
const scenarioBundle = (bundle, profile, km) => ({
    ...bundle,
    facts: { ...bundle.facts, odometerKm: km },
    inspection: {
        grade7: profile.grade7,
        interior: profile.severity,
        engine: profile.severity,
        hybrid: profile.severity,
        tires: profile.severity,
        damageGroup1Index: 0,
        damageGroup2Index: 0,
    },
    // A quick range prices the vehicle, not the deal: expected work and trade-in support come later.
    costItems: [],
    tradeIn: null,
});
const appraise = (policy, bundle, profile, km) => {
    const built = buildValuationInputs(scenarioBundle(bundle, profile, km));
    return runPolicy(policy, built.inputs, { tradeIn: false, costResolutions: built.costResolutions });
};
export const quickRange = ({ policy, bundle }) => {
    const odometer = bundle.facts.odometerKm;
    // One reference run gives `expectedKm` from the policy's own `age` and `expectedKm` equations, so a
    // changed age formula moves the range too (plan §5.11).
    const reference = appraise(policy, bundle, GOOD, odometer ?? 0);
    const expectedKm = reference.outputs.expectedKm ?? null;
    if (expectedKm === null || odometer === null || odometer === undefined) {
        return {
            expectedKm,
            band: null,
            lowKm: null,
            highKm: null,
            min: null,
            max: null,
            scenarios: [],
            incomplete: true,
        };
    }
    const stepKm = bundle.template.params.mileageStepKm ?? 0;
    const lowKm = Math.min(odometer, expectedKm);
    const highKm = Math.max(odometer, expectedKm + stepKm);
    const scenarios = [
        { key: 'GOOD_LOW', condition: 'GOOD', mileage: 'LOW', km: lowKm, appraisal: appraise(policy, bundle, GOOD, lowKm).outputs.appraisal ?? null },
        { key: 'GOOD_HIGH', condition: 'GOOD', mileage: 'HIGH', km: highKm, appraisal: appraise(policy, bundle, GOOD, highKm).outputs.appraisal ?? null },
        { key: 'POOR_LOW', condition: 'POOR', mileage: 'LOW', km: lowKm, appraisal: appraise(policy, bundle, POOR, lowKm).outputs.appraisal ?? null },
        { key: 'POOR_HIGH', condition: 'POOR', mileage: 'HIGH', km: highKm, appraisal: appraise(policy, bundle, POOR, highKm).outputs.appraisal ?? null },
    ];
    const priced = scenarios.map((scenario) => scenario.appraisal).filter((value) => value !== null);
    return {
        expectedKm,
        band: odometer <= expectedKm ? 'LOW' : 'HIGH',
        lowKm,
        highKm,
        min: priced.length > 0 ? Math.min(...priced) : null,
        max: priced.length > 0 ? Math.max(...priced) : null,
        scenarios,
        incomplete: priced.length < scenarios.length,
    };
};
//# sourceMappingURL=quick-range.js.map